import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  initiateEtransfer,
  fetchEtransferStatus,
  cancelEtransfer,
} from "../../functions/etransferApi";
import PaymentMethodPicker from "./PaymentMethodPicker";
import { trackEvent } from "../../lib/metaPixel";
import "./EtransferModal.css";

const POLL_MS = 15000;

/* ---- inline icons (no emoji, consistent 1.8 stroke) ---- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const IconBolt = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...stroke}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>
);
const IconCopy = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...stroke}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
);
const IconCheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...stroke}><path d="m20 6-11 11-5-5" /></svg>
);
const IconWarn = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
);
const IconClock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" {...stroke}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const IconSuccess = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>
);
const IconError = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 8v5m0 3h.01" /></svg>
);
const IconSend = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" {...stroke}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" /></svg>
);

function formatRemaining(ms) {
  if (ms <= 0) return "0s";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

const EtransferModal = ({
  showModal,
  handleClose,
  selectedBooking,
  toggleRefresh,
  paymentMethod,
  onSelectMethod,
}) => {
  const [phase, setPhase] = useState("loading"); // loading|instructions|waiting|success|expired|error
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // which field was copied
  const [now, setNow] = useState(Date.now());
  const modalRef = useRef(null);

  const bookingId = selectedBooking?.booking_id;

  // Create the pending e-Transfer when the modal opens.
  useEffect(() => {
    if (!showModal || !bookingId) return;
    let cancelled = false;
    setPhase("loading");
    initiateEtransfer(bookingId)
      .then((data) => {
        if (!cancelled) {
          setInfo(data);
          setPhase("instructions");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("We couldn't start your e-Transfer. Please close and try again.");
          setPhase("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showModal, bookingId]);

  // Focus the dialog on open + close on Escape.
  useEffect(() => {
    if (!showModal) return;
    modalRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, handleClose]);

  // 1s ticker for the expiry countdown.
  useEffect(() => {
    if (phase !== "instructions" && phase !== "waiting") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const poll = useCallback(async () => {
    try {
      const { status } = await fetchEtransferStatus(bookingId);
      if (status === "matched") {
        setPhase("success");
        if (info) {
          trackEvent("Purchase", {
            value: Number((info.amount / 100).toFixed(2)),
            currency: "CAD",
            content_type: "product",
            content_name: selectedBooking?.care_service_name,
            payment_method: "etransfer",
          });
        }
        toggleRefresh && toggleRefresh();
      } else if (status === "expired" || status === "cancelled") {
        setPhase("expired");
      }
    } catch {
      /* keep waiting */
    }
  }, [bookingId, toggleRefresh, info, selectedBooking]);

  useEffect(() => {
    if (phase !== "waiting") return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [phase, poll]);

  // Local expiry guard while the modal is open.
  const remainingMs = info ? new Date(info.expiresAt).getTime() - now : 0;
  useEffect(() => {
    if ((phase === "instructions" || phase === "waiting") && info && remainingMs <= 0) {
      setPhase("expired");
    }
  }, [phase, info, remainingMs]);

  if (!showModal) return null;

  const copy = (key, value) => {
    if (navigator.clipboard) navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
  };

  const CopyBtn = ({ field, value }) => (
    <button
      type="button"
      className={`ET-copy ${copied === field ? "is-copied" : ""}`}
      onClick={() => copy(field, value)}
      aria-label={`Copy ${field}`}
    >
      {copied === field ? <IconCheck /> : <IconCopy />}
      {copied === field ? "Copied" : "Copy"}
    </button>
  );

  const amountText = info ? `$${(info.amount / 100).toFixed(2)}` : "";

  return (
    <div className="ET-overlay" onClick={handleClose}>
      <div
        className="ET-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ET-title"
        tabIndex={-1}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ET-close" onClick={handleClose} aria-label="Close">
          <IconClose />
        </button>

        {onSelectMethod && (
          <PaymentMethodPicker selected={paymentMethod || "etransfer"} onSelect={onSelectMethod} />
        )}

        <div className="ET-body">
          {phase === "loading" && (
            <div className="ET-status">
              <div className="ET-status-icon is-wait"><div className="ET-spinner" /></div>
              <h2 className="ET-status-title" id="ET-title">Preparing your e-Transfer…</h2>
              <p className="ET-status-text">Generating a secure reference for this booking.</p>
            </div>
          )}

          {phase === "error" && (
            <div className="ET-status">
              <div className="ET-status-icon is-error"><IconError /></div>
              <h2 className="ET-status-title" id="ET-title">Something went wrong</h2>
              <p className="ET-status-text">{error}</p>
              <div className="ET-actions">
                <button className="ET-btn ET-btn-ghost" onClick={handleClose}>Close</button>
              </div>
            </div>
          )}

          {phase === "instructions" && info && (
            <>
              <div className="ET-head">
                <span className="ET-eyebrow"><IconBolt /> Interac e-Transfer</span>
                <h2 className="ET-title" id="ET-title">Send your payment</h2>
                <p className="ET-sub">
                  Open your bank, send an Interac e-Transfer with the details below.
                  We confirm automatically — no need to wait here.
                </p>
              </div>

              <div className="ET-code-card">
                <span className="ET-code-label">Reference code — put in the message</span>
                <strong className="ET-code-value">{info.referenceCode}</strong>
                <CopyBtn field="reference code" value={info.referenceCode} />
              </div>

              <div className="ET-rows">
                <div className="ET-row">
                  <span className="ET-row-label">Send to</span>
                  <span className="ET-row-value">{info.recipientEmail}</span>
                  <CopyBtn field="email" value={info.recipientEmail} />
                </div>
                <div className="ET-row">
                  <span className="ET-row-label">Exact amount</span>
                  <span className="ET-row-value ET-amount">{amountText}</span>
                  <CopyBtn field="amount" value={(info.amount / 100).toFixed(2)} />
                </div>
              </div>

              <div className="ET-note">
                <IconWarn />
                <span>
                  The reference code above <strong>must</strong> go in the e-Transfer message,
                  and the amount must match exactly. Otherwise confirmation is delayed.
                </span>
              </div>

              <div className="ET-timer">
                <IconClock /> Expires in {formatRemaining(remainingMs)}
              </div>

              <div className="ET-actions">
                <button className="ET-btn ET-btn-primary" onClick={() => setPhase("waiting")}>
                  I've sent it
                </button>
                <button type="button" className="ET-btn ET-btn-ghost" onClick={handleClose}>
                  Close
                </button>
              </div>
            </>
          )}

          {phase === "waiting" && (
            <div className="ET-status">
              <div className="ET-status-icon is-wait ET-pulse"><IconSend /></div>
              <h2 className="ET-status-title" id="ET-title">Waiting for your transfer…</h2>
              <p className="ET-status-text">
                We'll confirm by email and notification once it arrives — usually within a minute.
                It's safe to close this window.
              </p>
              {info && (
                <div className="ET-timer"><IconClock /> Expires in {formatRemaining(remainingMs)}</div>
              )}
              <div className="ET-actions">
                <button
                  type="button"
                  className="ET-btn ET-btn-ghost"
                  onClick={async () => {
                    await cancelEtransfer(bookingId).catch(() => {});
                    handleClose();
                  }}
                >
                  Cancel transfer
                </button>
              </div>
            </div>
          )}

          {phase === "success" && (
            <div className="ET-status">
              <div className="ET-status-icon is-success"><IconSuccess /></div>
              <h2 className="ET-status-title" id="ET-title">Payment confirmed</h2>
              <p className="ET-status-text">
                Your e-Transfer was received and your booking is paid. Thank you!
              </p>
              <div className="ET-actions">
                <button className="ET-btn ET-btn-primary" onClick={handleClose}>Done</button>
              </div>
            </div>
          )}

          {phase === "expired" && (
            <div className="ET-status">
              <div className="ET-status-icon is-error"><IconError /></div>
              <h2 className="ET-status-title" id="ET-title">This request expired</h2>
              <p className="ET-status-text">
                The payment window closed. Start the booking payment again to get a new code.
              </p>
              <div className="ET-actions">
                <button className="ET-btn ET-btn-ghost" onClick={handleClose}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EtransferModal;
